import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  RiDeleteBinLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiEyeLine,
  RiCloseLine,
  RiMailLine,
  RiPhoneLine,
  RiUserLine,
  RiCalendarLine,
} from "react-icons/ri";
import toast from "react-hot-toast";
import { confirmAndDelete } from "../../utils/confirmAndDelete";

const RequestCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 animate-pulse">
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-5 bg-gray-200 rounded w-32"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="w-8 h-8 bg-gray-200 rounded"></div>
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-full"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      </div>
      <div className="flex items-center space-x-4">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-4 bg-gray-200 rounded w-32"></div>
      </div>
    </div>
  </div>
);

const Requests = () => {
  const [requests, setRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const requestsPerPage = 6;

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/contacts/all`
      );
      setRequests(res.data);
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = requests.slice(
    indexOfFirstRequest,
    indexOfLastRequest
  );

  const totalPages = Math.ceil(requests.length / requestsPerPage);

  const openRequestModal = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const deleteRequest = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${process.env.REACT_APP_API_URL}/contacts/delete-contact`,
    });

    if (isConfirmed) {
      try {
        await fetchRequests();
        const newTotalPages = Math.ceil(
          (requests.length - 1) / requestsPerPage
        );
        if (currentPage > newTotalPages) {
          setCurrentPage(newTotalPages || 1);
        }

        Swal.fire({
          title: "Deleted!",
          text: "The request has been deleted.",
          icon: "success",
          confirmButtonColor: "#10B981",
        });
      } catch (err) {
        console.error("Error deleting request:", err);
        Swal.fire({
          title: "Error!",
          text: "Something went wrong.",
          icon: "error",
          confirmButtonColor: "#EF4444",
        });
      }
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="min-h-screen bg-transparent p-6">
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
            Contact Requests
          </h1>
          <div className="bg-primaryBtnColor text-white px-4 py-2 rounded-lg font-semibold">
            Total: {requests.length} requests
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {loading
            ? Array.from({ length: requestsPerPage }).map((_, i) => (
                <RequestCardSkeleton key={i} />
              ))
            : currentRequests.map((request) => (
                <div
                  key={request._id}
                  className="bg-white rounded-2xl overflow-hidden shadow-xl border border-primaryTextColor transition-transform hover:scale-[1.02] relative"
                >
                  <div className="absolute top-4 right-4 flex space-x-2 z-10">
                    <button
                      onClick={() => openRequestModal(request)}
                      className="p-2 bg-white bg-opacity-90 rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-colors shadow-md"
                      title="View Details"
                    >
                      <RiEyeLine size={16} />
                    </button>
                    <button
                      onClick={() => deleteRequest(request._id)}
                      className="p-2 bg-white bg-opacity-90 rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors shadow-md"
                      title="Delete Request"
                    >
                      <RiDeleteBinLine size={16} />
                    </button>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-full flex items-center justify-center text-white font-bold mr-4">
                        {request.firstName.charAt(0).toUpperCase()}
                        {request.lastName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-primaryTextColor">
                          {request.firstName} {request.lastName}
                        </h3>
                        <div className="flex items-center text-sm text-secondaryTextColor mb-1">
                          <RiMailLine size={14} className="mr-1" />
                          <span className="truncate">{request.email}</span>
                        </div>
                        <div className="flex items-center text-sm text-primaryBtnHoverColor">
                          <RiPhoneLine size={14} className="mr-1" />
                          <span>{request.mobileNo}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-secondaryTextColor min-h-12  line-clamp-2 text-sm leading-relaxed">
                        "{request.message}"
                      </p>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center">
                        <RiCalendarLine size={12} className="mr-1" />
                        <span>{formatDate(request.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
        </div>

        {/* Empty state */}
        {!loading && requests.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RiMailLine size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No requests yet
            </h3>
            <p className="text-gray-500">
              Contact form submissions will appear here when customers reach
              out.
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <button
              onClick={() => currentPage > 1 && paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              <RiArrowLeftSLine size={20} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`px-3 py-1 rounded-lg ${
                  currentPage === page
                    ? "bg-primaryBtnColor text-white"
                    : "bg-primaryTextColor text-gray-300 hover:bg-secondaryTextColor"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                currentPage < totalPages && paginate(currentPage + 1)
              }
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg ${
                currentPage === totalPages
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              <RiArrowRightSLine size={20} />
            </button>
          </div>
        )}

        {/* Request Details Modal */}
        {isModalOpen && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-300 shadow-xl">
              <div className="sticky top-0 bg-white p-4 border-b border-gray-300 flex justify-between items-center">
                <h2 className="text-xl font-bold text-primaryTextColor">
                  Request Details
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-primaryBtnHoverColor"
                >
                  <RiCloseLine size={24} />
                </button>
              </div>

              <div className="p-6">
                <div className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-2xl p-6 mb-6 text-white">
                  <div className="flex items-center mb-4">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white font-bold mr-4 text-xl">
                      {selectedRequest.firstName.charAt(0).toUpperCase()}
                      {selectedRequest.lastName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">
                        {selectedRequest.firstName} {selectedRequest.lastName}
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiMailLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Email Address
                        </span>
                      </div>
                      <p className="text-primaryTextColor">
                        {selectedRequest.email}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiPhoneLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Mobile Number
                        </span>
                      </div>
                      <p className="text-primaryTextColor">
                        {selectedRequest.mobileNo}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiUserLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Full Name
                        </span>
                      </div>
                      <p className="text-primaryTextColor">
                        {selectedRequest.firstName} {selectedRequest.lastName}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiCalendarLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Submitted On
                        </span>
                      </div>
                      <p className="text-primaryTextColor">
                        {formatDate(selectedRequest.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">Message</h4>
                  <div className="bg-white p-4 rounded-lg border-l-4 border-primaryBtnColor">
                    <p className="text-primaryTextColor leading-relaxed">
                      {selectedRequest.message}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-100 text-primaryTextColor py-2 px-6 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      deleteRequest(selectedRequest._id);
                      setIsModalOpen(false);
                    }}
                    className="bg-red-500 text-white py-2 px-6 rounded-lg hover:bg-red-600 transition-colors flex items-center"
                  >
                    <RiDeleteBinLine className="mr-2" size={16} />
                    Delete Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Requests;
